import {
  collectEnumValues,
  makeEnumTypeGuard,
} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {IndustriesEnum} from '../../../../models/api/userMetadataModel';

// Map Apollo-enriched industries to corresponding industry values according to
// the below spreadsheet:
// https://docs.google.com/spreadsheets/u/1/d/1d-e53vOo8Ykpnry9_5SkiKQImK7NQIa9vxYptItJPPs/edit?usp=drive_web&ouid=107699777239487978096
export function findFrontIndustryFromApolloIndustry(apolloIndustry?: string) {
  if (!apolloIndustry) {
    return undefined;
  }

  const cleanedApolloIndustry = apolloIndustry.trim().toLowerCase();
  const frontIndustry = collectEnumValues(IndustriesEnum).find((industryEnumValue) =>
    apolloIndustryToFrontIndustryMap[industryEnumValue].includes(cleanedApolloIndustry),
  );

  return frontIndustry || undefined;
}

const apolloIndustryToFrontIndustryMap = {
  [IndustriesEnum.ECOMMERCE]: [
    'consumer goods',
    'cosmetics',
    'fine art',
    'furniture',
    'glass, ceramics & concrete',
    'luxury goods & jewelry',
    'retail',
    'sporting goods',
    'wine and spirits',
  ],
  [IndustriesEnum.EVENTS]: [
    'events services',
    'mental health care',
    'performing arts',
    'recreational facilities and services',
  ],
  [IndustriesEnum.FINANCIAL_SERVICES]: [
    'accounting',
    'banking',
    'capital markets',
    'financial services',
    'insurance',
    'investment banking',
    'investment management',
    'venture capital & private equity',
  ],
  [IndustriesEnum.LOGISTICS]: [
    'import and export',
    'logistics and supply chain',
    'package/freight delivery',
    'transportation/trucking/railroad',
    'warehousing',
  ],
  [IndustriesEnum.MARKETING_ADVERTISING]: [
    'marketing and advertising',
    'online media',
    'public relations and communications',
    'publishing',
  ],
  [IndustriesEnum.MANUFACTURING]: [
    'aviation & aerospace',
    'building materials',
    'chemicals',
    'dairy',
    'defense & space',
    'electrical/electronic manufacturing',
    'farming',
    'food production',
    'industrial automation',
    'machinery',
    'mechanical or industrial engineering',
    'mining & metals',
    'nanotechnology',
    'packaging and containers',
    'paper & forest products',
    'plastics',
    'semiconductors',
    'shipbuilding',
    'tobacco',
  ],
  [IndustriesEnum.PROFESSIONAL_SERVICES]: [
    'alternative dispute resolution',
    'animation',
    'architecture & planning',
    'civil engineering',
    'consumer services',
    'environmental services',
    'executive office',
    'facilities services',
    'fund-raising',
    'government relations',
    'graphic design',
    'individual & family services',
    'judiciary',
    'law enforcement',
    'law practice',
    'legal services',
    'management consulting',
    'maritime',
    'medical practice',
    'photography',
    'professional training & coaching',
    'public policy',
    'security and investigations',
    'staffing and recruiting',
    'translation and localization',
    'veterinary',
    'writing and editing',
  ],
  [IndustriesEnum.SAAS_TECHNOLOGY]: [
    'computer & network security',
    'computer games',
    'computer hardware',
    'computer networking',
    'computer software',
    'consumer electronics',
    'e-learning',
    'education management',
    'human resources',
    'information technology and services',
    'internet',
    'market research',
    'music',
    'program development',
    'telecommunications',
    'wireless',
  ],
  [IndustriesEnum.TRAVEL_HOSPITALITY]: ['airlines/aviation', 'hospitality', 'leisure, travel & tourism'],
  [IndustriesEnum.HEALTH_FITNESS]: [
    'alternative medicine',
    'health, wellness and fitness',
    'hospital & health care',
    'medical devices',
    'pharmaceuticals',
  ],
  [IndustriesEnum.OTHER]: [
    'arts and crafts',
    'automotive',
    'broadcast media',
    'civic & social organization',
    'commercial real estate',
    'fishery',
    'gambling & casinos',
    'government administration',
    'international affairs',
    'legislative office',
    'libraries',
    'military',
    'motion pictures and film',
    'museums and institutions',
    'newspapers',
    'nonprofit organization management',
    'oil & energy',
    'outsourcing/offshoring',
    'philanthropy',
    'political organization',
    'primary/secondary education',
    'public safety',
    'railroad manufacture',
    'ranching',
    'religious institutions',
    'renewables & environment',
    'research',
    'restaurants',
    'supermarkets',
    'textiles',
    'think tanks',
    'utilities',
  ],
};

export function computeInitialDropdownSelection<T extends {[key: string]: string}>(
  enumObject: T,
  value: string | undefined,
) {
  if (!value) {
    return undefined;
  }

  // Only pre-populate the value if it matches the enum options.
  // (The server does not enforce dropdown's types beyond it being a string/number or null).
  const isEnumValue = makeEnumTypeGuard(enumObject);
  if (!isEnumValue(value)) {
    return undefined;
  }

  return value;
}
